import { CLOISTER_FLOOR } from '../../../site/cloisterLevels';
import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Landmark } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import type { LinearRGB } from '../../../core/color';
import { horizontalCap, orientedFace, planPoint, prismSolid, triangulateRing } from '../solids';
import { readBool } from './params';
import { buildChurchPlan, type ChurchColors, type PlannedChurch } from './church';
import { draftVessel, planFleche } from './churchPlan';
import { apseButtressReach } from './churchVessel';
import {
  dominantAxis,
  meshFrameOf,
  planBounds,
  planFrameOf,
  toLayout,
  type PlanFrame,
} from './planFrame';
import type { PlanRect } from './genericBlock';
import type { MasonryStyles } from './masonry';
import { SURFACE } from '../../mesh/attributes';
import { cloisterRanges, rearRooms, rectRing, CLOISTER_WALK } from './cloisterPlan';
import { buildCloisterArcade, arcadePiers } from './cloisterArcade';
import { buildArchBridge } from '../../terrain/archBridge';
import { subFrame } from '../solids';
import { buildCloisterRoom } from './cloisterRooms';

export const MONASTERY = {
  churchHeight: 12,
  ruinHeight: 7,
  ruinWall: 1,
  /** A traced church rectangle without a width still gets a nave. */
  churchWidthFallback: 10,
  cloister: {
    /** The walks' eaves, a storey over the garth, and the ranges' ridge over them. */
    eaveY: 3.6,
    pitch: 45 * (Math.PI / 180),
    /** A garth or a range the layout does not state. */
    rangeFallback: 9,
    /** The arcade's openings on the garth side: one bay every this many metres. */
    bayM: 3.2,
    opening: { width: 1.9, height: 2.5, sill: 0.4, proud: 0.06 },
    /** The garth as a levelled garden: over the ground and under it, so it shares no plane. */
    garthY: CLOISTER_FLOOR.garden,
    garthBaseY: -0.6,
  },
} as const;

/**
 * A convent is a precinct, not a chapel: the church, the cloister beside it and the garden, on the
 * land the layout traces. The church keeps the rectangle it was traced with (its own centre, length
 * and width in the precinct's frame); the cloister stands where the painting shows the convent's
 * green court, with the garth and range depths landmarks.md records from the woodcut.
 */
export interface MonasteryChurch {
  readonly u: number;
  readonly v: number;
  readonly length: number;
  readonly width: number;
}

export interface MonasteryCloister {
  readonly u: number;
  readonly v: number;
  readonly garth: number;
  readonly range: number;
}

export interface MonasteryParams {
  readonly ridgeTurret: boolean;
  readonly ruin: boolean;
  /** The convent church's own rectangle inside the precinct, when the layout gives one. */
  readonly church: MonasteryChurch | null;
  readonly cloister: MonasteryCloister | null;
}

function readOptional(p: Landmark['params'], key: string): number | null {
  const value = p[key];
  return typeof value === 'number' ? value : null;
}

export function parseMonasteryParams(
  landmark: Landmark,
  warn: (msg: string) => void
): MonasteryParams {
  const p = landmark.params;
  const churchLength = readOptional(p, 'churchLength');
  const church = churchLength
    ? {
        u: readOptional(p, 'churchU') ?? 0,
        v: readOptional(p, 'churchV') ?? 0,
        length: churchLength,
        width: readOptional(p, 'churchWidth') ?? MONASTERY.churchWidthFallback,
      }
    : null;
  const garth = readOptional(p, 'garth');
  const cloister = garth
    ? {
        u: readOptional(p, 'cloisterU') ?? 0,
        v: readOptional(p, 'cloisterV') ?? 0,
        garth,
        range: readOptional(p, 'range') ?? MONASTERY.cloister.rangeFallback,
      }
    : null;
  return {
    ridgeTurret: readBool(p, 'ridgeTurret', false, warn),
    ruin: readBool(p, 'ruin', false, warn),
    church,
    cloister,
  };
}

export interface MonasteryStyles {
  readonly church: ChurchColors;
  readonly ruin: MasonryStyles;
  /** The domestic ranges of the cloister: walls and tiled roofs. */
  readonly range: MasonryStyles;
  /** The garth's garden ground. */
  readonly garth: LinearRGB;
}

/**
 * Plan frame with the long axis along u, so the chapel's apse closes the long axis. A precinct whose
 * church and cloister the layout places itself keeps the frame of the rectangle it was traced with:
 * those offsets are written in that frame.
 */
function monasteryFrame(landmark: Landmark, placed: boolean): PlanFrame {
  /* A traced precinct is clipped by the streets, so its own dominant axis is not the one the church
     and cloister offsets are written in: those use the heading the record carries. */
  if (placed) return planFrameOf(landmark.footprint, landmark.heading);
  const axis = dominantAxis(landmark.footprint, landmark.heading);
  const first = planFrameOf(landmark.footprint, axis);
  const b = planBounds(first);
  return b.u1 - b.u0 >= b.v1 - b.v0 ? first : planFrameOf(landmark.footprint, axis + Math.PI / 2);
}

/** PLAN §7: a chapel is one vessel under a polygonal apse; its ridge turret is a needle. */
const CHAPEL_FORM = {
  naveHeight: MONASTERY.churchHeight,
  apse: 'polygonal',
  spire: 'needle',
} as const;

/*
 * The chapel fills its estimated rectangle exactly: the west gable stands on the west edge and the
 * apse buttresses reach the other three, where the church planner would fit a traced outline.
 */
function chapelPlan(frame: PlanFrame, ridgeTurret: boolean): PlannedChurch {
  const b = planBounds(frame);
  const reach = apseButtressReach();
  const half = (b.v1 - b.v0) / 2 - reach;
  const draft = draftVessel(CHAPEL_FORM, (b.v0 + b.v1) / 2, half, b.u0);
  const vessel = { ...draft, chordU: b.u1 - half - reach };
  const fleche = planFleche({ ...CHAPEL_FORM, ridgeTurret }, vessel, null, null);
  const plan = {
    tower: null,
    westBay: null,
    vessel,
    sides: [],
    chapels: [],
    transept: null,
    fleche,
  };
  return { plan, frame };
}

/** The church's traced rectangle, in layout metres, for its own plan frame. */
function churchRing(frame: PlanFrame, church: MonasteryChurch): Ring {
  const at = (u: number, v: number): Vec2 => toLayout(frame, [u, v]);
  const hu = church.length / 2;
  const hv = church.width / 2;
  return [
    at(church.u - hu, church.v - hv),
    at(church.u + hu, church.v - hv),
    at(church.u + hu, church.v + hv),
    at(church.u - hu, church.v + hv),
  ];
}

/** Roofless church shell: a wall ring with its top closed over the wall thickness. */
function buildRuin(builder: MeshBuilder, styles: MasonryStyles, frame: Frame, r: PlanRect): void {
  const t = MONASTERY.ruinWall;
  const outer: Ring = [
    [r.u0, r.v0],
    [r.u1, r.v0],
    [r.u1, r.v1],
    [r.u0, r.v1],
  ];
  const inner: Ring = [
    [r.u0 + t, r.v0 + t],
    [r.u0 + t, r.v1 - t],
    [r.u1 - t, r.v1 - t],
    [r.u1 - t, r.v0 + t],
  ];
  const h = MONASTERY.ruinHeight;
  prismSolid(builder, styles.wall, frame, outer, 0, h, { top: false, bottom: false });
  builder.prism(styles.wall, frame, inner, 0, h, { top: false, bottom: false, sides: true });
  for (const tri of triangulateRing(outer, [inner])) {
    orientedFace(
      builder,
      styles.wall,
      frame,
      tri.map((q) => planPoint(q[0], h, q[1])),
      [0, 1, 0]
    );
  }
}

/** A gable roof over a rectangle, its ridge along u or across it, closing the walls under it. */
function gableOver(
  builder: MeshBuilder,
  roof: VertexStyle,
  wall: VertexStyle,
  frame: Frame,
  r: PlanRect,
  eaveY: number,
  pitch: number,
  ridgeAlongU: boolean
): void {
  const half = (ridgeAlongU ? r.v1 - r.v0 : r.u1 - r.u0) / 2;
  const ridgeY = eaveY + half * Math.tan(pitch);
  const uc = (r.u0 + r.u1) / 2;
  const vc = (r.v0 + r.v1) / 2;
  const a: Vec3 = ridgeAlongU ? planPoint(r.u0, ridgeY, vc) : planPoint(uc, ridgeY, r.v0);
  const b: Vec3 = ridgeAlongU ? planPoint(r.u1, ridgeY, vc) : planPoint(uc, ridgeY, r.v1);
  const slope = (eave: [Vec3, Vec3], outward: Vec3): void => {
    orientedFace(builder, roof, frame, [eave[0], eave[1], b, a], outward);
  };
  if (ridgeAlongU) {
    slope([planPoint(r.u0, eaveY, r.v0), planPoint(r.u1, eaveY, r.v0)], [0, 1, 1]);
    slope([planPoint(r.u0, eaveY, r.v1), planPoint(r.u1, eaveY, r.v1)], [0, 1, -1]);
    orientedFace(
      builder,
      wall,
      frame,
      [planPoint(r.u0, eaveY, r.v0), planPoint(r.u0, eaveY, r.v1), a],
      [-1, 0, 0]
    );
    orientedFace(
      builder,
      wall,
      frame,
      [planPoint(r.u1, eaveY, r.v0), planPoint(r.u1, eaveY, r.v1), b],
      [1, 0, 0]
    );
  } else {
    slope([planPoint(r.u0, eaveY, r.v0), planPoint(r.u0, eaveY, r.v1)], [-1, 1, 0]);
    slope([planPoint(r.u1, eaveY, r.v0), planPoint(r.u1, eaveY, r.v1)], [1, 1, 0]);
    orientedFace(
      builder,
      wall,
      frame,
      [planPoint(r.u0, eaveY, r.v0), planPoint(r.u1, eaveY, r.v0), a],
      [0, 0, 1]
    );
    orientedFace(
      builder,
      wall,
      frame,
      [planPoint(r.u0, eaveY, r.v1), planPoint(r.u1, eaveY, r.v1), b],
      [0, 0, -1]
    );
  }
}

/** Covered walks, open arcade bays, rear rooms and one passage into the court. */
function buildCloister(
  builder: MeshBuilder,
  styles: MonasteryStyles,
  frame: Frame,
  c: MonasteryCloister,
  church: MonasteryChurch | null,
  edgeId: number
): void {
  const { garthY, garthBaseY, eaveY, pitch } = MONASTERY.cloister;
  const base = { edgeId, seed: 0, strokeAngle: 0 };
  const wall: VertexStyle = { ...styles.range.wall, ...base };
  const roof: VertexStyle = { ...styles.range.roof, ...base };
  for (const range of cloisterRanges(c, church)) {
    const r = range.rect;
    for (const room of rearRooms(range))
      buildCloisterRoom(builder, wall, styles.range, frame, room, eaveY - 0.05);
    buildCloisterArcade(builder, wall, frame, range);
    if (range.entrance) {
      const uc = (r.u0 + r.u1) / 2;
      const south = range.front[1][0] < range.front[0][0];
      const from = south ? r.v0 : r.v0 + CLOISTER_WALK.width;
      const to = south ? r.v1 - CLOISTER_WALK.width : r.v1;
      buildArchBridge(builder, wall, subFrame(frame, uc - 1.4, (from + to) / 2, 0), {
        length: 2.8,
        width: to - from,
        spans: [CLOISTER_WALK.door],
        pierWidth: 0.2,
        footY: -0.03,
        springY: 1.55,
        crownY: 2.8,
        parapet: { height: 0, thickness: 0 },
        deckY: () => eaveY - 0.06,
      });
    }
    gableOver(builder, roof, wall, frame, r, eaveY, pitch, range.ridgeAlongU);
    prismSolid(builder, wall, frame, rectRing(r), eaveY - 0.1, eaveY, {
      top: false,
      bottom: false,
    });
    horizontalCap(
      builder,
      { ...wall, surface: SURFACE.wood },
      frame,
      rectRing(r),
      eaveY - 0.1,
      false
    );
    prismSolid(
      builder,
      { ...wall, surface: SURFACE.street },
      frame,
      rectRing(r),
      -0.1,
      CLOISTER_WALK.floor,
      { top: true, bottom: true }
    );
  }
  const g = c.garth / 2;
  prismSolid(
    builder,
    { ...wall, color: styles.garth, surface: SURFACE.garden, extent: [c.garth, c.garth] },
    frame,
    rectRing({ u0: c.u - g, u1: c.u + g, v0: c.v - g, v1: c.v + g }),
    garthBaseY,
    garthY,
    { top: true, bottom: true }
  );
}

/** Reserve the precinct for generation, but collide with the actual buildings and arcade piers. */
export function monasteryObstacles(landmark: Landmark): Ring[] {
  const params = parseMonasteryParams(landmark, () => undefined);
  if (!params.cloister) return [landmark.footprint];
  const frame = monasteryFrame(landmark, true);
  const church = params.church ? churchRing(frame, params.church) : landmark.footprint;
  const ranges = cloisterRanges(params.cloister, params.church);
  return [
    church,
    ...ranges
      .flatMap((range) => [...rearRooms(range).map(rectRing), ...arcadePiers(range)])
      .map((ring) => ring.map((p) => toLayout(frame, p))),
  ];
}

export function buildMonastery(
  builder: MeshBuilder,
  landmark: Landmark,
  params: MonasteryParams,
  styles: MonasteryStyles,
  edgeId: number,
  seed: number
): number {
  const frame = monasteryFrame(landmark, params.church !== null || params.cloister !== null);
  const mesh = meshFrameOf(frame);
  const before = builder.triangleCount;
  const bounds = planBounds(frame);
  /* The church keeps the rectangle it was traced with; without one it fills the precinct, as the
     chapel-form builders have always drawn it. */
  const churchFrame = params.church
    ? planFrameOf(churchRing(frame, params.church), frame.heading)
    : frame;
  if (params.ruin) {
    buildRuin(builder, styles.ruin, mesh, bounds);
  } else {
    buildChurchPlan(
      builder,
      chapelPlan(churchFrame, params.ridgeTurret),
      styles.church,
      edgeId,
      seed
    );
  }
  if (params.cloister) buildCloister(builder, styles, mesh, params.cloister, params.church, edgeId);
  return builder.triangleCount - before;
}
